import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Select,
  Tag,
  Segmented,
  Alert,
  Space,
  Typography,
  Spin,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  SafetyCertificateOutlined,
  CheckCircleFilled,
  CopyOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  getBranches,
  getZatcaCrProfiles,
  getZatcaDevices,
  onboardZatcaDevice,
  renewZatcaDeviceCsid,
} from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../context/AppContextProvider";
import moment from "moment";
import { handleApiError, showAppError } from "../../../utils/errorHandler";
import {
  deviceNeedsZatcaReonboarding,
  isZatcaDeviceBlockingOnboarding,
} from "../../../utils/zatcaOnboarding";
import {
  ZatcaDeviceStatus,
  ZatcaEnvironment,
} from "../../../configs/constants";

const { Text } = Typography;
const FATOORA_PORTAL_URL = "https://fatoora.zatca.gov.sa";

const REQUIRED_KYC_FIELDS = [
  {
    backendKey: "business_arabic_name",
    frontendKey: "business_name_arabic",
  },
  {
    backendKey: "street_name",
    frontendKey: "street_name",
  },
  {
    backendKey: "building_number",
    frontendKey: "building_number",
  },
  {
    backendKey: "plot_identification",
    frontendKey: "plot_identification",
  },
  {
    backendKey: "district",
    frontendKey: "district",
  },
];

const getMissingKycFields = (user) =>
  REQUIRED_KYC_FIELDS.filter((field) => !user?.[field.backendKey]);

const statusTag = (status, t) => {
  const map = {
    [ZatcaDeviceStatus.Active]: {
      color: "var(--color-success)",
      label: t("active"),
    },
    [ZatcaDeviceStatus.CsidExpired]: {
      color: "var(--color-danger)",
      label: t("csid_expired"),
    },
    [ZatcaDeviceStatus.Inactive]: {
      color: "default",
      label: t("inactive"),
    },
    [ZatcaDeviceStatus.PendingOnboarding]: {
      color: "var(--color-info)",
      label: t("pending_onboarding"),
    },
    [ZatcaDeviceStatus.CompliancePending]: {
      color: "var(--color-info)",
      label: t("compliance_pending"),
    },
    [ZatcaDeviceStatus.CompliancePassed]: {
      color: "var(--color-success)",
      label: t("compliance_passed"),
    },
    [ZatcaDeviceStatus.NeedsReonboarding]: {
      color: "var(--color-warning)",
      label: t("needs_reonboarding"),
    },
  };
  const entry = map[status] || { color: "default", label: status || "—" };
  return <Tag color={entry.color}>{entry.label}</Tag>;
};

const formatExpiryDate = (iso) => {
  if (!iso)
    return { display: "—", level: "none", expired: false, daysLeft: null };
  const m = moment(iso);
  if (!m.isValid())
    return { display: "—", level: "none", expired: false, daysLeft: null };
  const daysLeft = m.diff(moment(), "days");
  let level = "ok"; // >60d
  if (daysLeft < 0) level = "expired";
  else if (daysLeft < 15) level = "danger";
  else if (daysLeft <= 60) level = "warning";
  return {
    display: m.format("YYYY-MM-DD"),
    level,
    expired: daysLeft < 0,
    daysLeft,
  };
};

const ZatcaOnboarding = () => {
  const { authUser } = useContext(AppContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [modalVisible, setModalVisible] = useState(false);
  const [loadingBTN, setLoadingBTN] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);

  const [devicesTableData, setDevicesTableData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [crProfilesData, setCrProfilesData] = useState([]);

  const [onboardForm] = Form.useForm();

  const missingKycFields = getMissingKycFields(authUser);
  const isKycComplete = missingKycFields.length === 0;

  const [renewModalVisible, setRenewModalVisible] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [selectedRenewDevice, setSelectedRenewDevice] = useState(null);
  const [renewForm] = Form.useForm();

  // ─── Fetch existing devices ────────────────────────────────────────────
  useEffect(() => {
    setIsDataLoading(true);
    getZatcaDevices()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          setDevicesTableData([]);
          return;
        }

        const payload = response?.data;
        const devices = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.data?.devices)
              ? payload.data.devices
              : [];
        setDevicesTableData(devices);
      })
      .catch((error) => {
        handleApiError(error, t);
        setDevicesTableData([]);
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, t]);

  // ─── Fetch branches + CR profiles once ─────────────────────────────────
  useEffect(() => {
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          setBranchesData([]);
          return;
        }

        setBranchesData(response.data?.data?.branches || []);
      })
      .catch((error) => {
        handleApiError(error, t);
        setBranchesData([]);
      });
    getZatcaCrProfiles()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          setCrProfilesData([]);
          return;
        }

        const data = response?.data?.data || response?.data;

        setCrProfilesData(data?.profiles || []);
      })
      .catch((error) => {
        handleApiError(error, t);
        setCrProfilesData([]);
      });
  }, [t]);

  // ─── Watched form values ──────────────────────────────────────────
  const watchedEnvironment = Form.useWatch("environment", onboardForm);
  const crOptions = (crProfilesData.length ? crProfilesData : branchesData)
    .filter((item) => item?.cr && item?.is_complete !== false)
    .map((item) => {
      const firstBranch = item?.branches?.[0];
      const arabicName =
        item?.business_arabic_name || item?.zatca_profile?.business_arabic_name;

      return {
        label: arabicName ? `${item.cr} - ${arabicName}` : item.cr,
        value: item.cr,
        cr: item.cr,
        zatca_cr_profile_id:
          item?.zatca_cr_profile_id || (item?.branches ? item.id : undefined),
        branch_id: firstBranch?.id || item?.branch_id || item?.id,
      };
    });

  const blockedCrNumbers = new Set(
    devicesTableData
      .filter((device) =>
        isZatcaDeviceBlockingOnboarding(device, watchedEnvironment),
      )
      .map((d) => d.zatca_cr_profile_id || d.cr)
      .filter(Boolean),
  );

  // ─── Show modal: pre-flight KYC check ─────────────────────────────────
  const showModal = () => {
    if (!authUser?.can_manage_zatca) {
      showAppError(t("zatca_permission_required_helper"));
      return;
    }
    onboardForm.resetFields();
    onboardForm.setFieldsValue({
      environment: ZatcaEnvironment.Sandbox,
    });
    setModalVisible(true);
  };

  const handleCancel = () => {
    if (loadingBTN) return;

    onboardForm.resetFields();
    setModalVisible(false);
  };

  const goToStoreConfig = () => {
    setModalVisible(false);
    navigate("/store-configuration");
  };

  const copyPortalUrl = () => {
    navigator.clipboard?.writeText(FATOORA_PORTAL_URL);
    NotificationWithIcon("success", t("zatca_portal_url_copied"));
  };

  const handleOnboard = () => {
    if (loadingBTN) return;
    onboardForm
      .validateFields()
      .then((values) => {
        const selectedCrOption = crOptions.find(
          (item) => item.value === values.cr,
        );

        const payload = {
          branch_id: selectedCrOption?.branch_id,
          environment: values.environment,
          otp: String(values.otp || "").replace(/\s+/g, ""),
        };

        let keepLoading = false;

        setLoadingBTN(true);
        onboardZatcaDevice(payload)
          .then((response) => {
            if (response.data?.success === true) {
              setReloadTableData((prev) => !prev);
              setModalVisible(false);
              onboardForm.resetFields();
              NotificationWithIcon(
                "success",
                t("device_onboarded"),
                t("zatca_onboard_success_helper", {
                  branch: values.cr,
                  environment: values.environment,
                }),
              );
            } else {
              handleApiError(response, t);
            }
          })
          .catch((info) => {
            const status = info?.response?.status;
            const errors = info?.response?.data?.data?.errors || {};

            // 403: missing can_manage_zatca permission.
            if (status === 403) {
              showAppError(t("zatca_permission_required_helper"));
              return;
            }

            // 422 with KYC-address errors → bounce back to store-config.
            const kycErrorKeys = REQUIRED_KYC_FIELDS.flatMap((field) => [
              field.backendKey,
              field.frontendKey,
            ]).concat(["vat", "cr"]);

            const hasKycError = kycErrorKeys.some((key) => key in errors);
            if (status === 422 && hasKycError) {
              showAppError(t("zatca_kyc_incomplete_helper"));
              setModalVisible(false);
              navigate("/store-configuration");
              return;
            }

            // 422 with branch_id error → branch doesn't belong to user; reset.
            if (status === 422 && errors.branch_id) {
              onboardForm.setFields([
                {
                  name: "cr",
                  errors: Array.isArray(errors.branch_id)
                    ? errors.branch_id
                    : [String(errors.branch_id)],
                },
              ]);
              onboardForm.resetFields(["cr"]);
              return;
            }

            // 422 with OTP errors (incl. expired) → focus + clear the OTP field.
            if (status === 422 && errors.otp) {
              onboardForm.setFields([
                {
                  name: "otp",
                  errors: Array.isArray(errors.otp)
                    ? errors.otp
                    : [String(errors.otp)],
                },
              ]);
              onboardForm.setFieldsValue({ otp: "" });
              return;
            }

            if (status === 422 && errors.compliance_failed) {
              showAppError(
                info?.response?.data?.message ||
                  t("zatca_onboarding_compliance_failed"),
              );
              return;
            }

            if (status === 503) {
              keepLoading = true;
              NotificationWithIcon(
                "warning",
                t("zatca_taking_longer_title"),
                t("zatca_taking_longer_helper"),
              );
              return;
            }

            // Generic fallback.
            const fieldErrors = Object.entries(errors).map(([name, msgs]) => ({
              name,
              errors: Array.isArray(msgs)
                ? msgs
                : Object.values(msgs || {}).flat(),
            }));
            if (fieldErrors.length) {
              onboardForm.setFields(fieldErrors);
              handleApiError(info, t);
            } else {
              handleApiError(info, t);
            }
          })
          .finally(() => {
            if (!keepLoading) setLoadingBTN(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const breadcrumbRoutes = [
    { path: "/", breadcrumbName: t("home"), icon: <HomeOutlined /> },
    {
      path: "zatca-onboarding",
      breadcrumbName: t("zatca_onboarding"),
      icon: <SafetyCertificateOutlined />,
    },
  ];

  const shouldShowRenewButton = (record) => {
    if (!record?.id) return false;
    if (record?.status === ZatcaDeviceStatus.CsidExpired) return true;
    const expiry = moment(record?.production_expires_at);
    if (!expiry.isValid()) return false;
    const daysLeft = expiry.diff(moment(), "days");
    return daysLeft >= 0 && daysLeft <= 30;
  };

  const openRenewModal = (record) => {
    setSelectedRenewDevice(record);
    renewForm.resetFields();
    setRenewModalVisible(true);
  };

  const handleRenewCsid = () => {
    renewForm.validateFields().then((values) => {
      setRenewLoading(true);

      renewZatcaDeviceCsid(selectedRenewDevice.id, {
        otp: String(values.otp || "").replace(/\s+/g, ""),
      })
        .then((response) => {
          if (response?.data?.success === false) {
            handleApiError(response, t);
            return;
          }

          NotificationWithIcon(
            "success",
            t("zatca_csid_renewed_title"),
            t("zatca_csid_renewed_helper"),
          );
          setRenewModalVisible(false);
          renewForm.resetFields();
          setReloadTableData((prev) => !prev);
        })
        .catch((error) => {
          handleApiError(error, t);
        })
        .finally(() => {
          setRenewLoading(false);
        });
    });
  };

  const DevicesTableColumns = [
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (branch) => branch?.name || "—",
    },
    {
      title: t("cr_number"),
      dataIndex: "cr",
      render: (cr) => cr || "—",
    },
    {
      title: t("status"),
      dataIndex: "status",
      render: (status, record) =>
        statusTag(
          deviceNeedsZatcaReonboarding(record)
            ? ZatcaDeviceStatus.NeedsReonboarding
            : status,
          t,
        ),
    },
    {
      title: t("invoices_count"),
      dataIndex: "current_icv",
      render: (count) => count || "—",
    },
    {
      title: t("csid_expiry"),
      dataIndex: "production_expires_at",
      render: (date) => {
        const f = formatExpiryDate(date);
        if (f.display === "—") return "—";

        let chip = null;
        if (f.level === "expired") {
          chip = (
            <Tag color="red" className="app-m-0">
              {t("csid_expired")}
            </Tag>
          );
        } else if (f.level === "danger") {
          chip = (
            <Tag color="red" className="app-m-0">
              {t("expiring_in_days", { count: f.daysLeft })}
            </Tag>
          );
        } else if (f.level === "warning") {
          chip = (
            <Tag color="orange" className="app-m-0">
              {t("expiring_in_days", { count: f.daysLeft })}
            </Tag>
          );
        }

        return (
          <Space size={4} direction="vertical" style={{ lineHeight: 1.2 }}>
            <span>{f.display}</span>
            {chip}
          </Space>
        );
      },
    },
    {
      title: t("action"),
      key: "action",
      width: 85,
      align: "center",
      render: (_text, record) => (
        <Space>
          {shouldShowRenewButton(record) && (
            <Button
              size="small"
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
                openRenewModal(record);
              }}
            >
              {t("renew_now")}
            </Button>
          )}

          <Button
            type="link"
            key="view"
            title={t("view_details")}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/zatca-onboarding/${record.id}`);
            }}
          >
            <EyeOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("zatca_onboarding")}
          ghost={false}
          extra={[
            <Button
              key="open-add-device-modal"
              type="primary"
              onClick={showModal}
              disabled={!authUser?.can_manage_zatca}
            >
              + {t("onboard_zatca")}
            </Button>,
          ]}
        />
      </Card>

      {/* Red banner above the table when one or more devices are expired. */}
      {(() => {
        const expiredCount = devicesTableData.filter((d) => {
          if (d.status === ZatcaDeviceStatus.CsidExpired) return true;
          const exp = d.production_expires_at
            ? moment(d.production_expires_at)
            : null;
          return exp && exp.isValid() && exp.isBefore(moment());
        }).length;
        if (expiredCount === 0) return null;
        return (
          <Alert
            type="error"
            showIcon
            className="app-mb-20"
            message={t("devices_cannot_submit_banner", {
              count: expiredCount,
            })}
          />
        );
      })()}
      <Card>
        <Table
          columns={DevicesTableColumns}
          dataSource={devicesTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>
      <Modal
        open={modalVisible}
        title={t("onboard_zatca")}
        onCancel={handleCancel}
        maskClosable={!loadingBTN}
        keyboard={!loadingBTN}
        closable={!loadingBTN}
        footer={[
          <Button
            type="default"
            key="back"
            onClick={handleCancel}
            disabled={loadingBTN}
          >
            {t("cancel")}
          </Button>,
          isKycComplete ? (
            <Button
              key="submit"
              type="primary"
              loading={loadingBTN}
              onClick={handleOnboard}
            >
              {t("enable_zatca")}
            </Button>
          ) : (
            <Button key="goto-config" type="primary" onClick={goToStoreConfig}>
              {t("complete_zatca_address_fields")}
            </Button>
          ),
        ]}
      >
        <Spin spinning={loadingBTN}>
          {!isKycComplete ? (
            <Alert
              type="warning"
              showIcon
              message={t("zatca_kyc_incomplete_title")}
              description={
                <div>
                  <p style={{ margin: "0 0 8px" }}>
                    {t("zatca_kyc_incomplete_helper")}
                  </p>
                  <Text strong>{t("missing_fields")}:</Text>
                  <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                    {missingKycFields.map((field) => (
                      <li key={field.backendKey}>{t(field.frontendKey)}</li>
                    ))}
                  </ul>
                </div>
              }
            />
          ) : (
            <>
              <Alert
                type="success"
                showIcon
                icon={<CheckCircleFilled />}
                message={t("zatca_kyc_complete_title")}
                className="app-mb-20"
              />

              <Form
                name="zatca-onboard-form"
                layout="vertical"
                form={onboardForm}
                onFinish={handleOnboard}
                initialValues={{ environment: ZatcaEnvironment.Sandbox }}
              >
                <Form.Item
                  name="cr"
                  label={t("cr_number")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_cr_number_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Select
                    disabled={loadingBTN}
                    showSearch
                    allowClear
                    placeholder={t("select_cr_number")}
                    options={crOptions.map((item) => {
                      const isBlocked =
                        blockedCrNumbers.has(item.zatca_cr_profile_id) ||
                        blockedCrNumbers.has(item.value);

                      return {
                        label: isBlocked
                          ? `${item.label} — ${t("already_onboarded")}`
                          : item.label,
                        value: item.value,
                        disabled: isBlocked,
                      };
                    })}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="environment"
                  label={t("environment")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_environment_required"),
                    },
                  ]}
                >
                  <Segmented
                    disabled={loadingBTN}
                    block
                    options={[
                      {
                        label: t("env_sandbox"),
                        value: ZatcaEnvironment.Sandbox,
                      },
                      {
                        label: t("env_simulation"),
                        value: ZatcaEnvironment.Simulation,
                      },
                      {
                        label: t("env_production"),
                        value: ZatcaEnvironment.Production,
                      },
                    ]}
                  />
                </Form.Item>

                <Alert
                  type="info"
                  className="app-mb-16"
                  message={t("get_your_otp")}
                  description={
                    <Space direction="vertical" size={4}>
                      <Text>{t("zatca_otp_instructions_helper")}</Text>
                      <Button
                        type="default"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={copyPortalUrl}
                      >
                        {t("copy_portal_url")}
                      </Button>
                    </Space>
                  }
                />

                <Form.Item
                  name="otp"
                  label={t("otp_6_digits")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_otp_required"),
                    },
                    {
                      pattern: /^\d{6}$/,
                      message: t("validate_otp_6_digits"),
                    },
                  ]}
                  hasFeedback
                  getValueFromEvent={(e) =>
                    String(e?.target?.value ?? "").replace(/\D+/g, "")
                  }
                >
                  <Input
                    maxLength={6}
                    inputMode="numeric"
                    placeholder="● ● ● ● ● ●"
                    style={{ letterSpacing: 4, textAlign: "center" }}
                    disabled={loadingBTN}
                  />
                </Form.Item>
              </Form>
            </>
          )}
        </Spin>
      </Modal>

      <Modal
        open={renewModalVisible}
        title={t("renew_csid")}
        onCancel={() => setRenewModalVisible(false)}
        maskClosable={false}
        footer={[
          <Button
            type="default"
            key="cancel"
            onClick={() => setRenewModalVisible(false)}
            disabled={renewLoading}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="renew"
            type="primary"
            loading={renewLoading}
            onClick={handleRenewCsid}
          >
            {t("renew_now")}
          </Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          message={t("get_your_otp")}
          description={t("zatca_get_otp_helper")}
          className="app-mb-16"
        />

        <Form form={renewForm} layout="vertical">
          <Form.Item
            name="otp"
            label={t("otp")}
            rules={[
              { required: true, message: t("validate_otp_required") },
              { len: 6, message: t("validate_otp_6_digits") },
            ]}
          >
            <Input maxLength={6} placeholder={t("enter_otp")} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ZatcaOnboarding;
